import React, { useState, useEffect, useCallback } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import FloatingButton from './components/FloatingButton';
import ChatWindow from './components/ChatWindow';
import WidgetAPI from './services/api';
import {
  ActionHandlers,
} from './services/actionHandlers';
import { getPageContext, shouldShowGlow } from './utils/contextDetector';
import {
  WidgetConfig,
  WidgetState,
  MilesMessage,
  MessageType,
  FormId,
  PageContext,
  TestDriveFormData,
  TradeAppraisalFormData,
  FinanceLinkFormData,
  ContactRequestFormData,
  MorePhotosFormData,
  Lead,
  OnboardingState,
  ViewType,
} from './types';
import {
  validatePhone,
  validateName,
  formatPhoneNumber,
  IsNotNullOrEmpty,
} from './utils/validation';
import { Vehicle } from './components/MilesCarousel';
import { getUserTimezone } from './utils/timezone';
import { containsTimeSlotOptions } from './utils/dateTimeDetector';
import { CarType, LeadActionType, PurchaseType } from './enum';
import RightSidePanel from './components/RightSidePanel';
import useToggle from './hooks/useToggle';
import PropertySpotlightDialog from './components/PropertySpotlight/PropertySpotlightDialog';
import PropertyDetailsDialog from './components/PropertySpotlight/PropertyDetailsDialog';
import PropertyInventoryDialog from './components/PropertySpotlight/PropertyInventoryDialog';
import PropertyTypeSelectionDialog from './components/PropertySpotlight/PropertyTypeSelectionDialog';

interface MilesWidgetProps {
  config: WidgetConfig;
}

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
  },
});

const validViews: ViewType[] = [
  'welcome',
  'onboard',
  'otp',
  'enhance',
  'chat',
  'forbidden',
];

const isViewType = (value: unknown): value is ViewType =>
  typeof value === 'string' && validViews.includes(value as ViewType);

const MilesWidget: React.FC<MilesWidgetProps> = ({ config }) => {
  const [api] = useState(() => new WidgetAPI(config));
  const [actionHandlers] = useState(
    () => new ActionHandlers(config.apiBase, config.agentId)
  );
  const [pageContext, setPageContext] = useState<PageContext | null>(null);

  const [state, setState] = useState<WidgetState>({
    isOpen: false,
    isAuthenticated: api.isAuthenticated(),
    isAnonymousAccess: api.isAnonymousAccess(),
    // currentView: api.checkIsWebAppUser() ? 'welcome' : 'enhance', // Start with chat view (allow immediate messaging)
    currentView: 'welcome',
    conversationId: api.getConversationId(),
    messages: [],
    isLoading: false,
    isSending: false,
    currentFlow: undefined,
    shouldGlow: false,
    currentVehicle: null,
    isForbidden: false,
    userCoverImage: undefined,
    showFloatingWidget: config.floatingButtonType !== 'side_panel',
    isLiveAgentView: false,
    hasActiveSalesMan: false,
    dealerRecentCars: [],
  });

  const [isTyping, setIsTyping] = useState(false);
  const [requestId, setRequestId] = useState<string>('');
  const [pendingMessage, setPendingMessage] = useState<string>('');
  const [activeForm, setActiveForm] = useState<FormId | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [tradeFlowVehicleData, setTradeFlowVehicleData] = useState<any>(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [testDriveRequestMessage, setTestDriveRequestMessage] =
    useState<string>('');

  // Conversational onboarding state
  const [onboardingState, setOnboardingState] = useState<OnboardingState>({
    step: 'idle',
    data: {},
  });

  const [selectedCarType, setSelectedCarType] = useState<CarType>(CarType.new);
  const [selectedCarData, setSelectedCarData] = useState<any>(undefined);

  const {
    toggle: autoSpotlightToggle,
    // onOpen: autoSpotlightOnOpen,
    onClose: autoSpotlightOnClose,
  } = useToggle();

  const {
    toggle: carTypeSelectionToggle,
    onOpen: carTypeSelectionOnOpen,
    onClose: carTypeSelectionOnClose,
  } = useToggle();

  const {
    toggle: carInventoryToggle,
    onOpen: carInventoryOnOpen,
    onClose: carInventoryOnClose,
  } = useToggle();

  const {
    toggle: carDetailsToggle,
    onOpen: carDetailsOnOpen,
    onClose: carDetailsOnClose,
  } = useToggle();

  // Test drive request detection function
  const isTestDriveRequest = useCallback((message: string): boolean => {
    const testDriveKeywords = [
      'test drive',
      'testdrive',
      'test-drive',
      'schedule',
      'book',
      'appointment',
      'come see',
      'visit',
      'come by',
    ];

    const lowerMessage = message.toLowerCase();
    return testDriveKeywords.some((keyword) => lowerMessage.includes(keyword));
  }, []);

  // Detect page context on mount
  useEffect(() => {
    const context = getPageContext();
    setPageContext(context);

    const glow = shouldShowGlow(context.type);
    setState((prev) => ({ ...prev, shouldGlow: glow }));
  }, []);

  useEffect(() => {
    const handleSetView = (event: Event) => {
      const customEvent = event as CustomEvent<{ view?: unknown }>;
      const nextView = customEvent.detail?.view;

      if (!isViewType(nextView)) {
        if (nextView !== undefined) {
          console.warn(
            '[Miles AI Widget] Ignoring invalid view:',
            nextView
          );
        }
        return;
      }

      setState((prev) => {
        if (prev.isForbidden && nextView !== 'forbidden') {
          return prev;
        }
        if (prev.currentView === nextView) {
          return prev;
        }
        return { ...prev, currentView: nextView };
      });
    };

    window.addEventListener('miles-widget-set-view', handleSetView);
    return () => {
      window.removeEventListener('miles-widget-set-view', handleSetView);
    };
  }, []);
  
  useEffect(() => {
    const handleShowInventory = () => {
      handleViewInventoryClick();
    };

    window.addEventListener('miles-widget-show-inventory', handleShowInventory);
    return () => {
      window.removeEventListener('miles-widget-show-inventory', handleShowInventory);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    const getDealerDetail = async () => {
      if (config.agentId) {
        const dealerDetail = await api.getDealer();
        const isForbidden = !(
          dealerDetail?.planStatus === true &&
          dealerDetail?.purchaseType === PurchaseType.subscription
        );

        setState((prev) => ({
          ...prev,
          isForbidden: isForbidden,
          currentView: isForbidden ? 'forbidden' : prev.currentView,
          userCoverImage: dealerDetail?.userCoverImage,
          hasActiveSalesMan: dealerDetail?.hasActiveSalesMan ?? false,
        }));
      }
    };

    const getTwoRecentCars = async () => {
      if (config.agentId) {
        const carData = await api.getProperties({ agentId: config.agentId, limit: 2, isPropertyPhotoRequired: true });

        if (IsNotNullOrEmpty(carData?.data)) {
          setState((prev) => ({
            ...prev,
            dealerRecentCars: carData?.data ?? [],
          }))
        }
      }
    }

    getDealerDetail();
    getTwoRecentCars();
  }, [api, config.agentId]);

  // Socket message listener for both authenticated and guest users
  useEffect(() => {
    if (!state.conversationId) {
      return;
    }

    const handleNewMessage = (message: MilesMessage) => {
      // Check if message belongs to this conversation
      if (message.conversationId === state.conversationId) {
        // Only add Miles messages (user messages are already in UI)
        if (message.senderType === 'miles') {
          setIsTyping(false);

          // If backend explicitly requires login for an action (e.g. scheduleShowing),
          // trigger onboarding before doing anything else.
          if (
            message.metadata?.requireLogin === true &&
            !state.isAuthenticated
          ) {
            // For test drive scheduling, try to recover the original user request
            let userRequestMessage = message.metadata?.originalMessage || '';

            if (!userRequestMessage) {
              const testDriveKeywords = [
                'test drive',
                'testdrive',
                'test-drive',
                'schedule',
                'book',
                'appointment',
                'come see',
                'visit',
                'come by',
              ];

              const testDriveMessage = state.messages
                .slice()
                .reverse()
                .find(
                  (msg) =>
                    msg.senderType === 'user' &&
                    testDriveKeywords.some((keyword) =>
                      msg.message.toLowerCase().includes(keyword)
                    )
                );

              userRequestMessage = testDriveMessage?.message || '';
            }

            // If this is a schedule test drive action and we still don't have
            // a clear user request, use a sensible default so that after
            // authentication we can reliably trigger the time picker.
            const requiresTestDriveFlow =
              message.metadata?.actionType === 'schedule_test_drive' ||
              message.metadata?.actionType === 'scheduleShowing' ||
              message.metadata?.actionType === 'scheduleTestDrive';

            if (!userRequestMessage && requiresTestDriveFlow) {
              userRequestMessage = 'test drive appointment';
            }

            if (userRequestMessage) {
              setPendingMessage(userRequestMessage);
            }

            // Switch to onboarding/login view
            setState((prev) => ({ ...prev, currentView: 'onboard' }));

            setTimeout(() => {
              toast.info(
                'Please verify your contact detail to continue'
              );
            }, 500);

            // Do not process time slot picker until after authentication
            return;
          }

          // CRITICAL FIX: Check if AI message contains time slot options (numbered list)
          // This detects when AI sends "1. Tomorrow at 11:00 AM" etc.
          const hasTimeSlotOptions = containsTimeSlotOptions(message.message);

          // Check if backend set showTimeSlots flag in metadata OR if message has time options
          if (message.metadata?.showTimeSlots === true || hasTimeSlotOptions) {
            // Get the test drive request message from metadata (sent by backend) or find the original request
            let userRequestMessage = message.metadata?.originalMessage || '';

            // If not in metadata, look for a user message that contains test drive keywords
            if (!userRequestMessage) {
              const testDriveKeywords = [
                'test drive',
                'testdrive',
                'test-drive',
                'schedule',
                'book',
                'appointment',
                'come see',
                'visit',
                'come by',
              ];
              const testDriveMessage = state.messages
                .slice()
                .reverse()
                .find(
                  (msg) =>
                    msg.senderType === 'user' &&
                    testDriveKeywords.some((keyword) =>
                      msg.message.toLowerCase().includes(keyword)
                    )
                );

              userRequestMessage =
                testDriveMessage?.message || 'test drive appointment';
            }

            // CRITICAL: Check authentication status before showing time picker
            if (!state.isAuthenticated) {
              // Store the pending test drive request
              setPendingMessage(userRequestMessage);

              // Show authentication flow
              setState((prev) => ({ ...prev, currentView: 'onboard' }));

              // Notify user why we need authentication
              setTimeout(() => {
                toast.info(
                  'Please provide your contact information to schedule your test drive'
                );
              }, 500);

              return; // Don't show time picker yet
            }

            // User is authenticated - show time picker
            setTestDriveRequestMessage(userRequestMessage);

            // Show helpful toast to guide user to time picker
            setTimeout(() => {
              toast.info(
                '👇 Please use the time picker below to select your appointment'
              );
            }, 500);
          }

          // Handle server-driven form opening via openForm metadata
          // For guest users: trigger onboarding to collect contact info
          // For authenticated users: dealer already has their info, no dialog needed
          if (message.metadata?.openForm) {
            if (message.metadata?.requireLogin && !state.isAuthenticated) {
              setState((prev) => ({ ...prev, currentView: 'onboard' }));

              setTimeout(() => {
                toast.info(
                  'Please verify your contact details to continue.'
                );
              }, 500);
            }
          }

          setState((prev) => {
            const newMessages = [...prev.messages, message];
            const newState = {
              ...prev,
              messages: newMessages,
              isSending: false,
            };
            return newState;
          });
        }
      }
    };

    let isActive = true;

    const setupListener = async () => {
      try {
        await api.initSocket();
        if (isActive) {
          api.onMessage(handleNewMessage);
        }
      } catch (error) {
        console.error(
          '[Miles Widget] Failed to initialize message listener:',
          error
        );
        // If socket fails, stop typing animation
        setIsTyping(false);
        setState((prev) => ({ ...prev, isSending: false }));
      }
    };

    setupListener();

    return () => {
      isActive = false;
      api.offMessage(handleNewMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, state.conversationId, state.isAuthenticated]);

  // Load conversation history
  const loadConversationHistory = useCallback(async () => {
    if (!state.conversationId) return;

    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const result = await api.loadMessages(state.conversationId);
      setState((prev) => {
        return {
          ...prev,
          messages: result.data || [],
          isLoading: false,
        };
      });
    } catch (error: any) {
      console.error('Failed to load messages:', error);
      toast.error(error.message || 'Failed to load message history.');
      setState((prev) => ({ ...prev, isLoading: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, state.conversationId]);

  useEffect(() => {
    if (
      state.isOpen &&
      state.conversationId &&
      state.currentView === 'chat' &&
      state.messages.length === 0 &&
      !isAuthenticating
    ) {
      loadConversationHistory();
    }
  }, [
    state.isOpen,
    state.conversationId,
    state.currentView,
    loadConversationHistory,
    state.messages.length,
    isAuthenticating,
  ]);

  const handleToggle = () => {
    if (!state.isOpen) {
      autoSpotlightOnClose();
      carTypeSelectionOnClose();
      carInventoryOnClose();
      carDetailsOnClose();
    }

    if (state.isOpen && state.currentView === 'enhance') {
      setState((prev) => ({ ...prev, currentView: 'welcome' }));
    } else {
      setState((prev) => ({ ...prev, isOpen: !prev.isOpen }));
    }
  };

  const handleClose = () => {
    setState((prev) => ({ ...prev, isOpen: false }));
    setTestDriveRequestMessage('');
  };

  const handleBack = () => {
    setState((prev) => ({
      ...prev,
      currentView:
        (prev?.currentView === 'enhance' ||
          prev?.currentView === 'onboard' ||
          prev?.currentView === 'otp') &&
          state.messages &&
          state.messages.length > 0
          ? 'chat'
          // : api.checkIsWebAppUser() ? 'welcome' : 'enhance',
          : 'welcome',
      currentFlow: undefined,
    }));
    setActiveForm(null);
    setTradeFlowVehicleData(null);
  };

  const handleSendMessage = async (
    message: string,
    metadata?: any,
    messageType: MessageType = 'text',
    overrideConversationId?: string
  ) => {
    let conversationId = overrideConversationId || state.conversationId;

    if (!state.isAnonymousAccess && !state.isAuthenticated) {
      setState((prev) => ({ ...prev, isLoading: true }));
      await api.anonymousLogin();
      setState((prev) => ({ ...prev, isLoading: false, isAnonymousAccess: true }));
    }

    // Create guest conversation if none exists
    if (!conversationId) {
      try {
        setState((prev) => ({ ...prev, isLoading: true }));
        const result = await api.createGuestConversation();
        conversationId = result.conversationId;
        api.setGuestConversation(conversationId);
        setState((prev) => ({
          ...prev,
          conversationId,
          isLoading: false,
        }));
      } catch (error: any) {
        console.error(
          '[Miles Widget] Failed to create guest conversation:',
          error
        );
        toast.error('Failed to start conversation. Please try again.');
        setState((prev) => ({ ...prev, isLoading: false }));
        return;
      }
    }

    if (state.currentView !== 'chat') {
      setState((prev) => ({ ...prev, currentView: 'chat' }));
    }

    // Check if we're in conversational onboarding mode
    if (
      onboardingState.step !== 'idle' &&
      onboardingState.step !== 'completed'
    ) {
      // Add user message to UI
      const userMessage: MilesMessage = {
        id: `temp-${Date.now()}`,
        conversationId: conversationId,
        senderType: 'user',
        message,
        messageType,
        createdAt: new Date().toISOString(),
        metadata,
      };

      setState((prev) => ({
        ...prev,
        messages: [...prev.messages, userMessage],
      }));

      // Handle onboarding input
      await handleOnboardingInput(message);
      return;
    }

    // Add user message to UI first
    const userMessage: MilesMessage = {
      id: `temp-${Date.now()}`,
      conversationId: conversationId,
      senderType: 'user',
      message,
      messageType,
      createdAt: new Date().toISOString(),
      metadata,
    };

    setState((prev) => ({
      ...prev,
      messages: [...prev.messages, userMessage],
    }));

    // Send message to backend for AI response (all intent handling is now server-side)
    setState((prev) => ({ ...prev, isSending: true }));
    setIsTyping(true);
    setActiveForm(null);

    try {
      await api.sendMessage(conversationId, message, metadata, messageType);
    } catch (error: any) {
      console.error('Failed to send message:', error);
      toast.error(error.message || 'Failed to send message. Please try again.');
      setIsTyping(false);
    } finally {
      setState((prev) => ({ ...prev, isSending: false }));
    }
  };

  // Handle high-intent flows (test drive, finance, etc.)
  /*
  const handleHighIntentFlow = async (
    message: string,
    intentId: string,
    intentResult: any,
    overrideConversationId?: string
  ) => {
    let conversationId = overrideConversationId || state.conversationId!;

    // If not authenticated, show onboarding form
    if (!state.isAuthenticated && !overrideConversationId) {
      // Store the pending high-intent action (e.g., test drive request)
      setPendingMessage(message);

      // Transition to onboarding form view
      setState((prev) => ({ ...prev, currentView: 'onboard' }));

      return;
    }

    // User is authenticated
    // For test drive (INT-25), show the time picker directly
    if (intentId === 'INT-25') {
      setTestDriveRequestMessage(message);
      return;
    }

    // For other high-intent actions, send response
    if (intentResult.suggestedResponse) {
      await sendMilesResponse(conversationId, intentResult.suggestedResponse);
    }

    // // User is authenticated, show the form
    // const formId = getFormForIntent(intentId);

    // if (formId) {
    //   setTimeout(() => {
    //     setActiveForm(formId);
    //   }, 500);
    // }
  };*/

  // handleInventoryFilter removed — inventory filtering is now handled server-side

  // Send Miles' response as a message
  const sendMilesResponse = async (
    conversationId: string,
    response: string,
    metadata?: any
  ) => {
    const milesMessage: MilesMessage = {
      id: `miles-${Date.now()}`,
      conversationId,
      senderType: 'miles',
      message: response,
      messageType: metadata ? 'action' : 'text',
      createdAt: new Date().toISOString(),
      metadata,
    };

    setState((prev) => ({
      ...prev,
      messages: [...prev.messages, milesMessage],
    }));
  };

  // Start conversational onboarding
  // const startConversationalOnboarding = async (triggeringIntent: string, triggeringMessage: string) => {

  //   setOnboardingState({
  //     step: 'collecting_name',
  //     data: {},
  //     triggeringIntent,
  //     triggeringMessage,
  //   });

  //   // Ask for name
  //   await sendMilesResponse(
  //     state.conversationId!,
  //     "I'll need to collect some information from you. What's your name?"
  //   );
  // };

  // Handle user input during conversational onboarding
  const handleOnboardingInput = async (userInput: string) => {
    const { step } = onboardingState;
    const conversationId = state.conversationId!;

    switch (step) {
      case 'collecting_name': {
        const validation = validateName(userInput, 'Name');
        if (!validation.valid) {
          await sendMilesResponse(conversationId, validation.error!);
          return;
        }

        // Split name into first and last
        const nameParts = userInput.trim().split(/\s+/);
        const firstName = nameParts[0];
        const lastName =
          nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

        setOnboardingState((prev) => ({
          ...prev,
          step: 'collecting_phone',
          data: { ...prev.data, firstName, lastName },
        }));

        await sendMilesResponse(
          conversationId,
          `Thanks ${firstName}! What's your mobile number?`
        );
        break;
      }

      case 'collecting_phone': {
        const validation = validatePhone(userInput);
        if (!validation.valid) {
          await sendMilesResponse(conversationId, validation.error!);
          return;
        }

        // Remove all non-numeric characters for storage and API
        const phoneDigits = userInput.replace(/\D/g, '');

        // Format for display only
        const formattedPhone = formatPhoneNumber(userInput);

        // Auto-generate email from phone number
        const autoGeneratedEmail = `${phoneDigits}@qrbuddy.ai`;

        setOnboardingState((prev) => ({
          ...prev,
          step: 'collecting_tcpa',
          data: {
            ...prev.data,
            phone: phoneDigits, // Store unformatted (digits only)
            email: autoGeneratedEmail,
          },
        }));

        // Show TCPA consent message with action buttons (use formatted for display)
        await sendMilesResponse(
          conversationId,
          `By clicking "I Agree", you consent to receive text messages from ${config.agentName} at ${formattedPhone}. Message and data rates may apply.`,
          {
            actions: [
              { label: 'I Agree', action: 'tcpa_agree' },
              { label: 'Cancel', action: 'tcpa_cancel' },
            ],
          }
        );
        break;
      }

      default:
        break;
    }
  };

  // Handle TCPA consent action
  const handleTCPAAction = async (action: string) => {
    const conversationId = state.conversationId!;

    if (action === 'tcpa_cancel') {
      // Reset onboarding
      setOnboardingState({ step: 'idle', data: {} });
      await sendMilesResponse(
        conversationId,
        'No problem! Let me know if you change your mind.'
      );
      return;
    }

    if (action === 'tcpa_agree') {
      // User agreed to TCPA
      setOnboardingState((prev) => ({
        ...prev,
        data: { ...prev.data, tcpaConsent: true },
      }));

      await sendMilesResponse(
        conversationId,
        'Thanks! Sending verification code to your phone...'
      );

      // Call identify API
      setState((prev) => ({ ...prev, isLoading: true }));

      try {
        const result = await api.sendIdentity({
          firstName: onboardingState.data.firstName!,
          lastName: onboardingState.data.lastName!,
          email: onboardingState.data.email!,
          phone: onboardingState.data.phone!,
        });

        if (result.accessToken) {
          // Direct authentication (existing user)
          await handleDirectAuthentication(result);
        } else if (result.requestId) {
          // OTP required (new user) - show OTP component
          setRequestId(result.requestId!);

          // Reset onboarding state and transition to OTP view
          setOnboardingState({ step: 'idle', data: {} });

          setState((prev) => ({
            ...prev,
            currentView: 'otp',
            isLoading: false,
          }));

          toast.success('Verification code sent to your phone!');
        }

        setState((prev) => ({ ...prev, isLoading: false }));
      } catch (error: any) {
        console.error('Failed to send identity:', error);
        toast.error(
          error.message || 'Failed to send verification code. Please try again.'
        );
        setState((prev) => ({ ...prev, isLoading: false }));

        // Reset onboarding on error
        setOnboardingState({ step: 'idle', data: {} });
      }
    }
  };

  // Handle direct authentication (existing user)
  const handleDirectAuthentication = async (result: any) => {
    const newConversationId =
      result.conversationId || state.conversationId || null;
    const conversationChanged = newConversationId !== state.conversationId;
    setIsAuthenticating(true);

    api.setAuth(
      result.accessToken,
      newConversationId || undefined,
      result.user
    );
    setState((prev) => ({
      ...prev,
      isAuthenticated: true,
      conversationId: newConversationId,
      messages: conversationChanged ? [] : prev.messages,
    }));

    // Reset onboarding state
    setOnboardingState({ step: 'idle', data: {} });

    toast.success('Successfully authenticated!');

    await sendMilesResponse(newConversationId, 'All set! How can I help you?');

    // Handle pending message if exists
    if (pendingMessage) {
      if (isTestDriveRequest(pendingMessage)) {
        // Check if pending message is a test drive request
        setTimeout(() => {
          setTestDriveRequestMessage(pendingMessage);
          setPendingMessage('');
          setIsAuthenticating(false);
        }, 500);
      } else {
        // For other intents, send message normally
        setTimeout(() => {
          if (newConversationId) {
            handleSendMessage(
              pendingMessage,
              undefined,
              'text',
              newConversationId
            );
          }
          setTimeout(() => setIsAuthenticating(false), 1000);
        }, 300);
        setPendingMessage('');
      }
    } else {
      setIsAuthenticating(false);
    }
  };

  // Handle form submissions
  const handleTestDriveSubmit = async (data: TestDriveFormData) => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const result = await api.requestLeadAction({
        propertyId: state.currentVehicle?.id,
        actionType: LeadActionType.scheduleShowing,
        scheduleDate: data.preferredDateTime || null,
      });

      if (result) {
        toast.success('Showing request submitted!');
        setActiveForm(null);

        // Send confirmation message
        await sendMilesResponse(
          state.conversationId!,
          'Great — the team will confirm your time and text you the details.'
        );

        // Optional: Send SMS confirmation
        // await actionHandlers.sendSMS(data.phone, 'test_drive_confirmation', { ...data });
      } else {
        toast.error(result.message || 'Failed to submit request');
      }
    } catch (error: any) {
      console.error('Test drive submit error:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const handleTradeAppraisalSubmit = async (data: TradeAppraisalFormData) => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const result = await api.requestLeadAction({
        propertyId: state.currentVehicle?.id,
        actionType: LeadActionType.requestPropertyValuation,
      });

      if (result) {
        toast.success('Valuation request submitted!');
        setActiveForm(null);
        setTradeFlowVehicleData(null);

        await sendMilesResponse(
          state.conversationId!,
          'Got it - the team will review it and reach out soon.'
        );
      } else {
        toast.error(result.message || 'Failed to submit request');
      }
    } catch (error: any) {
      console.error('Trade appraisal submit error:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const handleFinanceLinkSubmit = async (data: FinanceLinkFormData) => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const result = await api.requestLeadAction({
        propertyId: state.currentVehicle?.id,
        actionType: LeadActionType.requestFinance,
      });

      if (result) {
        toast.success('Finance application link sent to your phone!');
        setActiveForm(null);

        await sendMilesResponse(
          state.conversationId!,
          "Got it! I just sent the application link to your phone. Let me know if you need help with anything while you're filling it out."
        );
      } else {
        toast.error(result.message || 'Failed to submit request');
      }
    } catch (error: any) {
      console.error('Finance link submit error:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const handleContactRequestSubmit = async (data: ContactRequestFormData) => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const result = await api.requestLeadAction({
        propertyId: state.currentVehicle?.id,
        actionType: LeadActionType.requestLiveAgent,
      });

      if (result) {
        toast.success('Contact request submitted!');
        setActiveForm(null);

        await sendMilesResponse(
          state.conversationId!,
          'Thanks! Someone from the team will reach out shortly.'
        );
      } else {
        toast.error(result.message || 'Failed to submit request');
      }
    } catch (error: any) {
      console.error('Contact request submit error:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const handleMorePhotosSubmit = async (data: MorePhotosFormData) => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const result = await api.requestLeadAction({
        propertyId: state.currentVehicle?.id,
        actionType: LeadActionType.requestPricing,
      });

      if (result) {
        // Notify staff via SMS or internal system
        toast.success('Photo request submitted!');
        setActiveForm(null);

        await sendMilesResponse(
          state.conversationId!,
          'Thanks! The team will text you some more photos soon.'
        );
      } else {
        toast.error(result.message || 'Failed to submit request');
      }
    } catch (error: any) {
      console.error('More photos submit error:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const handleSubmitOnboarding = async (data: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    timezone?: string;
    tcpaConsent: boolean;
  }) => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      if (!data.email.trim()) {
        // Remove all non-numeric characters for storage and API
        const phoneDigits = data.phone.replace(/\D/g, '');

        // Auto-generate email from phone number
        data.email = `${phoneDigits}@qrbuddy.ai`;
      }

      const timezone = getUserTimezone();
      if (timezone && timezone !== 'UTC') {
        data.timezone = timezone;
      }

      const result = await api.sendIdentity(data);

      if (result.accessToken) {
        const newConversationId =
          result.conversationId || state.conversationId || null;
        const conversationChanged = newConversationId !== state.conversationId;

        // Set authenticating flag to prevent loadConversationHistory from running
        setIsAuthenticating(true);

        api.setAuth(
          result.accessToken,
          newConversationId || undefined,
          result.user
        );
        setState((prev) => ({
          ...prev,
          isAuthenticated: true,
          currentView: 'chat',
          conversationId: newConversationId,
          // Clear messages if conversation ID changed (switched from guest to authenticated conversation)
          messages: conversationChanged ? [] : prev.messages,
          isLoading: false,
        }));

        toast.success('Successfully authenticated!');

        // Handle pending message (high-intent action)
        if (pendingMessage) {
          if (isTestDriveRequest(pendingMessage)) {
            // Check if pending message is a test drive request
            setTimeout(() => {
              setTestDriveRequestMessage(pendingMessage);
              setPendingMessage('');
              setIsAuthenticating(false);
            }, 500);
          } else {
            // For other messages, re-send to backend
            setTimeout(() => {
              if (newConversationId) {
                handleSendMessage(
                  pendingMessage,
                  undefined,
                  'text',
                  newConversationId
                );
              }
              // Clear authenticating flag after message is sent
              setTimeout(() => setIsAuthenticating(false), 1000);
            }, 300);
            setPendingMessage('');
          }
        } else {
          // No pending message, clear flag immediately
          setIsAuthenticating(false);
        }
      } else if (result.requestId) {
        setRequestId(result.requestId);
        setState((prev) => ({
          ...prev,
          currentView: 'otp',
          isLoading: false,
        }));
        toast.success('Verification code sent to your phone!');
      }
    } catch (error: any) {
      console.error('Failed to submit onboarding:', error);
      toast.error(error.message || 'Failed to submit. Please try again.');
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const handleVerifyOTP = async (otp: string) => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const result = await api.verifyOTP(requestId, otp);
      const newConversationId = result.conversationId || null;
      const conversationChanged = newConversationId !== state.conversationId;

      // Set authenticating flag to prevent loadConversationHistory from running
      setIsAuthenticating(true);

      api.setAuth(
        result.accessToken,
        newConversationId || undefined,
        result.user
      );

      setState((prev) => ({
        ...prev,
        isAuthenticated: true,
        currentView: 'chat',
        conversationId: newConversationId,
        // Clear messages if conversation ID changed (switched from guest to authenticated conversation)
        messages: conversationChanged ? [] : prev.messages,
        isLoading: false,
      }));

      toast.success('Phone verified successfully!');

      // Handle pending message (high-intent action)
      if (pendingMessage) {
        if (isTestDriveRequest(pendingMessage)) {
          // Check if pending message is a test drive request
          setTimeout(() => {
            setTestDriveRequestMessage(pendingMessage);
            setPendingMessage('');
            setIsAuthenticating(false);
          }, 500);
        } else {
          // For other messages, re-send to backend
          setTimeout(() => {
            if (newConversationId) {
              handleSendMessage(
                pendingMessage,
                undefined,
                'text',
                newConversationId
              );
            }
            // Clear authenticating flag after message is sent
            setTimeout(() => setIsAuthenticating(false), 1000);
          }, 300);
          setPendingMessage('');
        }
      } else {
        // No pending message, clear flag immediately
        setIsAuthenticating(false);
      }

      if (state.isLiveAgentView) {
        api.requestLeadAction({
          propertyId: state.currentVehicle?.id,
          actionType: LeadActionType.requestLiveAgent,
          scheduleDate: null,
      });
      }
    } catch (error: any) {
      console.error('Failed to verify OTP:', error);
      toast.error(error.message || 'Invalid code. Please try again.');
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const handleEnhanceClick = (currentVehicle?: Vehicle) => {
    setState((prev) => ({
      ...prev,
      currentView: api.isShowEnhanceView() ? 'enhance' : 'chat',
      currentVehicle: currentVehicle ? currentVehicle : null,
    }));
  };
  const handleChangeView = (_view: ViewType) => {
    setState((prev) => ({
      ...prev,
      currentView: _view,
    }));
  }
  const handleOnOpenCarTypeSelection = () => {
    carTypeSelectionOnOpen();
    handleClose();
    setState((prev) => ({
      ...prev,
      currentView: 'welcome',
    }));
  }

  const handleViewInventoryClick = () => {
    if (carTypeSelectionToggle) return;

    carTypeSelectionOnOpen();

    setState((prev) => ({
      ...prev,
      showFloatingWidget: true,
      // isOpen: true,
    }));
  }

  const handleCloseWebRailClick = () => {
    setState((prev) => ({
      ...prev,
      showFloatingWidget: true,
      // isOpen: true,
    }));
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastContainer
        position='top-right'
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme='light'
        style={{ zIndex: 2147483648 }}
      />

      {state.showFloatingWidget && <FloatingButton
        onClick={handleToggle}
        isOpen={state.isOpen}
        hasUnread={state.shouldGlow}
        unreadCount={!state.conversationId ? 1 : 0}
        iconUrl={`${config.webUrl}/static/miles_icon.png`}
      />}

      {!state.showFloatingWidget && <RightSidePanel
        config={config}
        vehicleImageUrl={state.userCoverImage}
        onTry={() => {
          handleViewInventoryClick();
        }}
        onClose={() => {
          handleCloseWebRailClick();
        }}
      />}

      <ChatWindow
        isOpen={state.isOpen}
        onClose={handleClose}
        currentView={state.currentView}
        messages={state.messages}
        onSendMessage={handleSendMessage}
        onBack={handleBack}
        onSubmitOnboarding={handleSubmitOnboarding}
        onVerifyOTP={handleVerifyOTP}
        isTestDriveRequest={isTestDriveRequest}
        isSending={state.isSending}
        isLoading={state.isLoading}
        isTyping={isTyping}
        iconUrl={`${config.webUrl}/static/miles_icon.png`}
        hasConversation={!!state.conversationId && state.isAuthenticated}
        webUrl={config.webUrl}
        config={config}
        api={api}
        testDriveRequestMessage={testDriveRequestMessage}
        isAuthenticated={state.isAuthenticated}
        isForbidden={state.isForbidden}
        // New props for forms
        activeForm={activeForm}
        onCloseForm={() => setActiveForm(null)}
        onTestDriveSubmit={handleTestDriveSubmit}
        onTradeAppraisalSubmit={handleTradeAppraisalSubmit}
        onFinanceLinkSubmit={handleFinanceLinkSubmit}
        onContactRequestSubmit={handleContactRequestSubmit}
        onMorePhotosSubmit={handleMorePhotosSubmit}
        vehicleContext={pageContext?.vehicleContext}
        onAction={handleTCPAAction}
        onEnhanceClick={handleEnhanceClick}
        currentVehicle={state.currentVehicle}
        userCoverImage={state.userCoverImage}
        handleChangeView={handleChangeView}
        onOpenCarTypeSelection={handleOnOpenCarTypeSelection}
        tradeFlowVehicleData={tradeFlowVehicleData}
        isLiveAgentView={state.isLiveAgentView}
        hasActiveSalesMan={state.hasActiveSalesMan}
        onVehicleClick={(selectedVehicle?: any) => {
          setSelectedCarData(selectedVehicle);
          carDetailsOnOpen();
          handleClose();
        }}
        updateLocalState={(newState: any) => {
          setState((prev) => ({
            ...prev,
            ...newState || {}
          }));
        }}
      />

      {autoSpotlightToggle && (
        <PropertySpotlightDialog
          open={autoSpotlightToggle}
          onClose={autoSpotlightOnClose}
          config={config}
        />
      )}

      {carTypeSelectionToggle && (
        <PropertyTypeSelectionDialog
          config={config}
          open={carTypeSelectionToggle}
          onClose={carTypeSelectionOnClose}
          onBack={carTypeSelectionOnClose}
          dealerRecentCars={state.dealerRecentCars}
          onCarTypeSelected={(carType: CarType) => {
            setSelectedCarType(carType);
            carInventoryOnOpen();

          }}
        />
      )}

      {carInventoryToggle && (
        <PropertyInventoryDialog
          open={carInventoryToggle}
          onClose={carInventoryOnClose}
          onBack={carInventoryOnClose}
          onCarSelected={(carData: any) => {
            setSelectedCarData(carData);
            carDetailsOnOpen();
            carInventoryOnClose();
            carTypeSelectionOnClose();
          }}
          carType={selectedCarType}
          config={config}
        />
      )}

      {carDetailsToggle && (
        <PropertyDetailsDialog
          open={carDetailsToggle}
          onClose={() => {
            carDetailsOnClose();
            setSelectedCarData(undefined);
            // carTypeSelectionOnOpen();
            handleToggle();
          }}
          selectedCarData={selectedCarData}
          config={config}
          updateLocalState={(newState: any) => {
            setState((prev) => ({
              ...prev,
              ...newState || {}
            }));
          }}
        />
      )}
    </ThemeProvider>
  );
};

export default MilesWidget;
